<script setup>
/* eslint semi: 1 */
import { computed, ref, onMounted, onUnmounted } from 'vue';
import Draggable from 'vuedraggable';
import MediaItemPreview from '@/components/common/media/media-item-preview.vue';
import { useInlineNameEdit } from '@/composables/media/use-inline-name-edit';
import { calculateLedCardLayout } from '@/composables/media/use-media-list-page';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  showLoader: {
    type: Boolean,
    default: false,
  },
  orientation: {
    type: String,
    default: 'horizontal',
    validator: (v) => ['horizontal', 'vertical'].includes(v),
  },
  isAudioPicker: {
    type: Boolean,
    default: false,
  },
  currentAudioPlaying: {
    type: Object,
    default: () => ({
      isPlaying: false,
      audio: null
    }),
  },
  hasSelection: {
    type: Boolean,
    default: false,
  },
  selectedIds: {
    type: Set,
    default: () => new Set(),
  },
  enableDelete: {
    type: Boolean,
    default: false,
  },
  enableCopy: {
    type: Boolean,
    default: false,
  },
  enableEditProject: {
    type: Boolean,
    default: false,
  },
  /**
   * When set, switches the grid into LED/Videowall mode where each card is
   * sized by JS-computed pixel dimensions derived from the resolution's aspect
   * ratio rather than CSS fractional widths.
   * Shape: { width: number, height: number }
   */
  ledTag: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'card-click',
  'delete-click',
  'copy-click',
  'edit-click',
  'video-play',
  'image-preview',
  'audio-play',
  'audio-pause',
  'svg-load',
  'iframe-mouseenter',
  'iframe-mouseleave',
  'drag-tag-to-media',
  'edit-name'
]);

const { editingResourceId, startEditNameMode, confirmEditName, optimisticNames } = useInlineNameEdit();

// ResizeObserver on the wrapper to track live container width.
const containerRef = ref(null);
const containerWidth = ref(0);
let resizeObserver = null;

// In audio-picker mode the layout is always horizontal regardless of `orientation`.
const isVertical = computed(() => props.orientation === 'vertical' && !props.isAudioPicker);

// Whether LED/Videowall pixel-based sizing is active.
const isLedMode = computed(() => !!props.ledTag);

/**
 * Replicates the `calculateAspectRatioFit({ ledPage: true })` logic from utils.js.
 * Computes card pixel dimensions and gutter from the selected LED tag's aspect ratio
 * and the live container width tracked by the ResizeObserver above.
 *
 * AR-based items-per-row table (mirrors the old media page):
 *   AR ≥ 5:1             → 1 per row
 *   AR < 5:1  and ≥ 2:1  → 2
 *   AR < 2:1  and ≥ 4:3  → 3
 *   AR < 4:3  and ≥ 3:4  → 4
 *   AR < 3:4  and ≥ 1:2  → 5
 *   AR < 1:2             → 6
 */
const ledCardLayout = computed(() => {
  if (!isLedMode.value || !containerWidth.value) return null;
  // 14px margin per column, same constants as utils.js
  return calculateLedCardLayout(containerWidth.value, props.ledTag, 14);
});

/**
 * Computed bezels mask from the selected LED tag and the
 * live card dimensions from ledCardLayout, passed to
 * MediaItemPreview for bezel overlay rendering.
 */
const resolvedBezelsMask = computed(() => {
  if (!props.ledTag || !ledCardLayout.value) return null;

  return {
    bezels: props.ledTag.bezels,
    resolutionWidth: props.ledTag.width,
    resolutionHeight: props.ledTag.height,
    cardWidth: ledCardLayout.value.cardWidth,
    cardHeight: ledCardLayout.value.cardHeight,
  };
});

/**
 * Returns true when `index` is the last card in its row (no gutter should be
 * applied to the right side of that card).
 * @param {number} index - 0-based position in the items array
 */
function isLastInRow (index) {
  if (!ledCardLayout.value) return false;
  return (index + 1) % ledCardLayout.value.itemsPerRow === 0;
}

// Preview container dimensions.
// Audio-picker: fixed-height full-width row (the column width is the whole container).
// Grid: fills the grid column width; height is derived from the original aspect ratios
//   (97×55 landscape / 55×97 portrait from .media-preview-fixed in media-list.scss)
//   so width and height always scale together as the viewport changes.
// LED/Videowall: height is controlled via inline style from ledCardLayout;
//   only the width class is applied here.
const previewDimClasses = computed(() => {
  if (props.isAudioPicker) {
    return 'w-full h-[42px] min-[1920px]:h-[70px]';
  }
  if (isLedMode.value) {
    return 'w-full'; // explicit height applied via :style from ledCardLayout
  }
  return isVertical.value
    ? 'w-full aspect-[55/97]'   // portrait  ≈ 55 : 97
    : 'w-full aspect-[97/55]';  // landscape ≈ 97 : 55
});

// Click-capture overlay that covers exactly the preview area.
const overlayPositionClasses = computed(() => {
  if (props.isAudioPicker) return 'absolute inset-0';
  // LED mode has no inner p-2 padding, so no offset needed
  if (isLedMode.value) return 'absolute top-0 left-0 right-0 bottom-[20px]';
  return 'absolute top-2 left-2 right-2 bottom-[calc(8px_+_20px)]'; // +20px for the name text
});

/**
 * Hook function for draggable as dropzone for `tags-shared` group.
 * @param {Object} event - VueDraggable add event
 * @param {Object} media - Media item
 */
function handleTagDraggableAdd (event, media) {
  emit('drag-tag-to-media', { event, media });
}

/**
 * Handles the blur event from the inline name input.
 * Calls confirmEditName to get the rename payload, then emits 'edit-name'
 * so the parent can call the API and revert via mediaStore.clearOptimisticName on failure.
 * @param {FocusEvent} event
 */
function handleConfirmEditName (event) {
  const result = confirmEditName(event);
  if (!result) return;
  emit('edit-name', result);
}

onMounted(() => {
  if (containerRef.value) {
    resizeObserver = new ResizeObserver(entries => {
      containerWidth.value = entries[0]?.contentRect.width ?? 0;
    });
    resizeObserver.observe(containerRef.value);
  }
});

onUnmounted(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});
</script>

<template>
  <div ref="containerRef" class="media-grid-list-wrapper">
    <div class="media-grid-list-container flex flex-wrap">
      <template v-if="showLoader">
        <div
          v-for="i in (ledCardLayout ? ledCardLayout.itemsPerRow : 4)"
          :key="i"
          data-component-element-id="media-list-item-loader"
          :class="['relative box-border p-0 select-none h-auto', isLedMode ? '' : 'w-[calc(25%)]']"
          :style="ledCardLayout ? {
            width: `${ledCardLayout.cardWidth}px`,
            marginBottom: '14px',
            ...ledCardLayout?.itemsPerRow > 1
              ? { marginRight: i < (ledCardLayout?.itemsPerRow ?? 4) ? `${ledCardLayout.gutter}px` : '0' }
              : { marginInline: 'auto' }
          } : {}"
        >
          <div :class="['box-border relative w-auto h-auto', isAudioPicker ? 'p-0' : 'pt-2 pl-2 pr-4 pb-4']">
            <div
              :class="['relative overflow-hidden animate-pulse bg-white', previewDimClasses]"
              :style="ledCardLayout ? { height: `${ledCardLayout.cardHeight}px` } : {}"
            />
          </div>
        </div>
      </template>

      <!-- Real items -->
      <template v-else-if="items?.length > 0">
        <Draggable
          v-for="(mediaItem, mediaIndex) in items"
          :key="mediaItem.resourceId"
          tag="div"
          item-key="resourceId"
          ghost-class="sortable-ghost"
          :id="mediaItem.resourceId"
          :class="[
            'media-item group relative box-border p-0 select-none h-auto',
            isAudioPicker ? 'w-[calc(100%-33px)] mb-[10px]' : (!isLedMode ? 'w-[calc(25%)]' : ''),
            mediaItem._edit?.notVisible ? 'hidden' : '',
            hasSelection && selectedIds.has(mediaItem.resourceId) ? 'opacity-50' : ''
          ]"
          :style="ledCardLayout ? {
            width: `${ledCardLayout.cardWidth}px`,
            marginBottom: '14px',
            ...ledCardLayout.itemsPerRow === 1
              ? { marginInline: 'auto' }
              : { marginRight: isLastInRow(mediaIndex) ? '0' : `${ledCardLayout.gutter}px` }
          } : {}"
          :list="[mediaItem]"
          :animation="250"
          :group="{ name: 'tags-shared', pull: false, put: true }"
          :data-component-element-id="`media-list-item-${mediaItem.resourceId}`"
          @add="handleTagDraggableAdd($event, mediaItem)"
        >
          <template #item="{ element: item }">
            <div class="relative">
              <div
                :class="[
                  'media-item-content-container',
                  isAudioPicker ? 'bg-[#cdeffb] p-0 rounded' : (isLedMode ? 'p-0' : 'p-2'),
                ]"
              >
                <div
                  :class="[
                    'media-item-content',
                    previewDimClasses,
                    item.disabled ? 'grayscale opacity-60' : '',
                    !isAudioPicker && item.preview?.type === 'img' ? 'checkboard-background' : '',
                    isAudioPicker && !item.disabled
                      ? 'group-hover:shadow-[0px_0px_8px_4px_rgba(0,87,164,1)]'
                      : ''
                  ]"
                  :style="ledCardLayout ? { height: `${ledCardLayout.cardHeight}px` } : {}"
                >
                  <MediaItemPreview
                    :item="item"
                    :isAudioPicker="isAudioPicker"
                    :isSelected="selectedIds.has(item.resourceId)"
                    :isAudioPlaying="currentAudioPlaying.isPlaying"
                    :isCurrentAudioPlaying="currentAudioPlaying.isPlaying && currentAudioPlaying.audio?.resourceId === item.resourceId"
                    :orientation="orientation"
                    :bezelsMask="resolvedBezelsMask"
                    @video-play="$emit('video-play', $event)"
                    @image-preview="$emit('image-preview', $event)"
                    @audio-play="$emit('audio-play', $event)"
                    @audio-pause="$emit('audio-pause', $event)"
                    @svg-load="$emit('svg-load', $event)"
                  />
                </div>

                <div :class="['media-list-item-name', {'disabled-media-item': item.disabled}]">
                  <!-- Name Txt -->
                  <span :class="['media-item-name', {'has-status': (item.new || item.updated) || (item._edit?.modifiedFile) }]">
                    <span v-if="item._edit?.modifiedFile && !item.new" class="status">{{$t('label.new')}}</span>
                    <span
                      v-if="editingResourceId !== item.resourceId"
                      class="cursor-text"
                      @click="startEditNameMode(item.resourceId)"
                    >
                      {{ optimisticNames[item.resourceId] ?? item.name }}
                    </span>
                    <input
                      v-else
                      class="w-full bg-transparent text-center border-none outline-none"
                      :value="item.name"
                      autofocus
                      @blur="handleConfirmEditName"
                      @keydown.enter="(e) => { e.target.blur(); }"
                    />

                  </span>
                </div>
              </div>

              <div
                :class="[
                  'z-[1] box-border',
                  overlayPositionClasses,
                  item.disabled ? 'cursor-auto' : 'cursor-pointer',
                  selectedIds.has(item.resourceId)
                    ? 'bg-white opacity-50'
                    : !item.disabled ? 'group-hover:bg-white group-hover:opacity-50' : '',
                ]"
                @click="$emit('card-click', item)"
                @mouseenter="$emit('iframe-mouseenter', item)"
                @mouseleave="$emit('iframe-mouseleave', item)"
              />

              <!-- Delete icon -->
              <div
                v-if="enableDelete"
                class="media-item-action-icon delete absolute top-4 right-4 z-[2] opacity-40 hover:opacity-100 transition-opacity"
                :class="hasSelection && selectedIds.has(item.resourceId) ? 'block' : 'hidden'"
                @click.stop="$emit('delete-click', item)"
              >
                <div class="icon" />
              </div>

              <!-- Copy icon -->
              <div
                v-if="enableCopy && ['svg', 'html'].includes(item.preview?.type) && !item.status && item.type !== 'audio' && selectedIds.size <= 1"
                class="media-item-action-icon copy absolute top-4 right-11 z-[2] opacity-40 hover:opacity-100 transition-opacity"
                :class="hasSelection && selectedIds.has(item.resourceId) ? 'block' : 'hidden'"
                @click.stop="$emit('copy-click', item)"
              >
                <div class="icon" />
              </div>

              <!-- Edit icon -->
               <div
                v-if="enableEditProject && ['svg', 'html'].includes(item.preview?.type) && !item.status && item.type !== 'audio' && selectedIds.size === 1"
                class="media-item-action-icon edit absolute top-4 right-18 z-[2] opacity-40 hover:opacity-100 transition-opacity"
                :class="hasSelection && selectedIds.has(item.resourceId) ? 'block' : 'hidden'"
                @click.stop="$emit('edit-click', item)"
              >
                <div class="icon" />
              </div>

              <div class="drag-and-drop-hover"></div>
            </div>
          </template>
        </Draggable>
      </template>

      <template v-else>
        <div class="flex flex-col items-center justify-center gap-[10px] w-full py-20">
          <p class="font-bold uppercase">{{$t('label.noSearchResults')}}</p>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@reference '#tailwind.scss';

.media-grid-list-wrapper {
  .media-grid-list-container {
    .media-item-action-icon {
      & {
        @apply w-6 h-6 p-0 cursor-pointer;
      }

      .icon {
        @apply w-full h-full p-0 bg-no-repeat bg-center;
        background-size: contain !important;
      }

      &.delete {
        .icon {
          background: url('../../../static/icons/remove.svg') no-repeat center center;
        }
      }

      &.copy {
        .icon {
          background: url('../../../static/icons/copy.svg') no-repeat center center;
        }
      }

      &.edit {
        .icon {
          background: url('../../../static/icons/edit.svg') no-repeat center center;
        }
      }
    }

    .checkboard-background {
      background-image:
        linear-gradient(45deg, #dddddd 25%, transparent 25%),
        linear-gradient(-45deg, #dddddd 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #dddddd 75%),
        linear-gradient(-45deg, transparent 75%, #dddddd 75%);
      background-size: 14px 14px !important;
      background-position: 0 0, 0 7px, 7px -7px, -7px 0px !important;
    }

    :deep(.media-item) {
      &.ui-droppable-hover {
        .drag-and-drop-hover {
          @apply block;
        }
      }

      .sortable-ghost {
        @apply hidden!;
      }

      .drag-and-drop-hover {
        & {
          @apply absolute hidden top-2 left-0 right-0 w-[calc(100%-16px)] h-[calc(100%-35px)] opacity-80 box-border rounded-sm mx-auto my-0;

          background: url('../../../static/images/gradient-background-md.png') no-repeat;
          background-size: cover;
          border: 1px dashed #2464f0;
        }
      }

      .media-item-content-container {
        @apply box-border relative cursor-default w-auto h-auto;

        .media-item-content {
          @apply relative overflow-hidden max-w-full bg-cover bg-[center_50%] rounded-sm shadow-[0_0_1px_rgba(0,0,0,0.4)];
        }
      }
    }
  }

  .media-list-item-name {
    @apply whitespace-nowrap overflow-hidden text-ellipsis text-graphite-grey text-xs leading-5 relative mb-0 mt-0;

    &.disabled-media-item {
      .media-item-name {
        @apply cursor-auto;
      }
    }

    .media-item-name {
      @apply w-full h-full cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis;

      span.status {
        & {
          @apply font-bold uppercase text-[#7818ee] font-black text-[11px] p-[4px_8px_0px_0px];
        }
      }
    }
  }
}
</style>
